import { useCallback } from 'react';
import { create } from 'zustand';

interface ChargeDetails {
  amount: number | null;
  description: string;
  paymentMethod: 'card' | 'cash' | 'check' | 'other';
  invoiceNumber?: string;
  receiptEmail: boolean;
  notes?: string;
  timestamp: string; // ISO timestamp for audit
}

interface QuickChargeState {
  isOpen: boolean;
  familyId: string | null;
  familyName: string | null;
  chargeDetails: ChargeDetails;
  auditLog: {
    action: string;
    timestamp: string;
    details: Record<string, any>;
  }[];
  openDialog: (familyId: string, familyName?: string) => void;
  closeDialog: () => void;
  updateChargeDetails: (details: Partial<ChargeDetails>) => void;
  resetChargeDetails: () => void;
  logAction: (action: string, details: Record<string, any>) => void;
}

const defaultChargeDetails: ChargeDetails = {
  amount: null,
  description: '',
  paymentMethod: 'card',
  receiptEmail: true,
  timestamp: new Date().toISOString()
};

export const useQuickChargeStore = create<QuickChargeState>((set) => ({
  isOpen: false,
  familyId: null,
  familyName: null,
  chargeDetails: { ...defaultChargeDetails },
  auditLog: [],
  
  openDialog: (familyId, familyName) => set({
    isOpen: true,
    familyId,
    familyName: familyName || null,
    chargeDetails: {
      ...defaultChargeDetails,
      timestamp: new Date().toISOString()
    }
  }),
  
  closeDialog: () => set({
    isOpen: false,
    familyId: null,
    familyName: null,
  }),
  
  updateChargeDetails: (details) => set((state) => ({
    chargeDetails: {
      ...state.chargeDetails,
      ...details
    }
  })),
  
  resetChargeDetails: () => set({
    chargeDetails: { ...defaultChargeDetails }
  }),
  
  logAction: (action, details) => set((state) => ({
    auditLog: [
      ...state.auditLog,
      {
        action,
        timestamp: new Date().toISOString(),
        details: {
          ...details,
          familyId: state.familyId,
          familyName: state.familyName,
        }
      }
    ]
  }))
}));

export const useQuickCharge = () => {
  const { 
    isOpen, 
    familyId, 
    familyName, 
    chargeDetails,
    auditLog,
    openDialog, 
    closeDialog,
    updateChargeDetails,
    resetChargeDetails,
    logAction
  } = useQuickChargeStore();

  const openQuickChargeDialog = useCallback((familyId: string, familyName?: string) => {
    openDialog(familyId, familyName);
    logAction('QuickChargeDialogOpened', { 
      familyId, 
      familyName, 
      timestamp: new Date().toISOString()
    });
  }, [openDialog, logAction]);

  const submitQuickCharge = useCallback(async () => {
    // Log the charge attempt
    logAction('QuickChargeSubmitted', { 
      amount: chargeDetails.amount,
      description: chargeDetails.description,
      paymentMethod: chargeDetails.paymentMethod,
      timestamp: new Date().toISOString()
    });

    // Here you would integrate with your payment processing system
    // For now, we'll simulate a successful charge
    return new Promise<boolean>((resolve) => {
      setTimeout(() => {
        const success = Math.random() > 0.1; // 90% success rate for demo
        
        if (success) {
          logAction('QuickChargeSuccess', { 
            amount: chargeDetails.amount,
            transactionId: `tx_${Math.random().toString(36).substring(2, 12)}`,
            timestamp: new Date().toISOString()
          });
        } else {
          logAction('QuickChargeFailed', { 
            amount: chargeDetails.amount,
            reason: 'Payment processing error',
            timestamp: new Date().toISOString()
          });
        }
        
        resolve(success);
      }, 1500);
    });
  }, [chargeDetails, logAction]);

  return {
    isQuickChargeOpen: isOpen,
    quickChargeFamilyId: familyId,
    quickChargeFamilyName: familyName,
    quickChargeDetails: chargeDetails,
    chargeAuditLog: auditLog,
    openQuickChargeDialog,
    closeQuickChargeDialog: closeDialog,
    updateQuickChargeDetails: updateChargeDetails,
    resetQuickChargeDetails: resetChargeDetails,
    submitQuickCharge,
    logQuickChargeAction: logAction
  };
};
